var _a;
import { BoxGeometry, BufferGeometry, Color, LineBasicMaterial, LineSegments, Matrix4, Mesh, MeshBasicMaterial, Object3D, Quaternion, Sphere, SphereGeometry, Uint8BufferAttribute, Vector3, } from "three";
import { Cartographic, Ellipsoid } from "../../math";
import { readBoundingBox, readBoundingSphere } from "../../data/formats/3d_tiles";
/**
 * Utility singleton that allows for one-shot rendering of various wireframe objects like lines, spheres, etc
 */
export class DebugDraw {
    static lineObjectInit() {
        const mesh = new LineSegments(DebugDraw.LINE_GEOMETRY, DebugDraw.LINE_MATERIAL);
        mesh.frustumCulled = false;
        return mesh;
    }
    /**
     * Renders a wireframe section of a spheroid with the given longitude, latitude and height bounds.
     * @param ellipsoid
     * @param minLongitude
     * @param minLatitude
     * @param maxLongitude
     * @param maxLatitude
     * @param minHeight
     * @param maxHeight
     * @param globeToWorldTransform
     * @param color
     */
    static addGeographicBounds(ellipsoid, minLongitude, minLatitude, maxLongitude, maxLatitude, minHeight, maxHeight, globeToWorldTransform, color = [255, 0, 0]) {
        const west = minLongitude;
        const south = minLatitude;
        const east = maxLongitude;
        const north = maxLatitude;
        const minH = minHeight;
        const maxH = maxHeight;
        // Draw the 4 vertical lines of the spheroid segment
        drawCartographicSegments(ellipsoid, {
            longitude: west,
            latitude: south,
            height: minH,
        }, {
            longitude: west,
            latitude: south,
            height: maxH,
        }, 1, globeToWorldTransform, color);
        drawCartographicSegments(ellipsoid, {
            longitude: west,
            latitude: north,
            height: minH,
        }, {
            longitude: west,
            latitude: north,
            height: maxH,
        }, 1, globeToWorldTransform, color);
        drawCartographicSegments(ellipsoid, {
            longitude: east,
            latitude: south,
            height: minH,
        }, {
            longitude: east,
            latitude: south,
            height: maxH,
        }, 1, globeToWorldTransform, color);
        drawCartographicSegments(ellipsoid, {
            longitude: east,
            latitude: north,
            height: minH,
        }, {
            longitude: east,
            latitude: north,
            height: maxH,
        }, 1, globeToWorldTransform, color);
        // Draw the bottom curves
        drawCartographicQuadAtHeight(ellipsoid, west, south, east, north, minH, 10, globeToWorldTransform, color);
        // Draw the top curves
        drawCartographicQuadAtHeight(ellipsoid, west, south, east, north, maxH, 10, globeToWorldTransform, color);
    }
    /**
     * Renders the bounding volume encoded in the given `BoundingVolume` item as a debug item. Will apply the given
     * transformation matrix to the bounding volume before rendering
     * @param boundingVolume
     * @param volumeToGlobeTransform Matrix that transforms the volume/tile into ECEF coordinates
     * @param globeToWorldTransform Matrix that transforms from ECEF into ThreeJS world space
     * @param color Optional color to apply to the volume
     */
    static addBoundingVolume(options, boundingVolume, volumeToGlobeTransform, globeToWorldTransform, color = [255, 0, 0]) {
        const position = new Vector3();
        const x = new Vector3();
        const y = new Vector3();
        const z = new Vector3();
        const sphere = new Sphere();
        // Render the bounding volume region if present and requested
        if (boundingVolume.region !== undefined &&
            boundingVolume.region !== null &&
            options.tileBoundingVolumesShowRegion) {
            // Directly referencing WGS_84 is correct here as the 3D tiles spec states all geodetic regions are defined
            // against WGS 84. Using another ellipsoid definition here makes no sense
            const region = boundingVolume.region;
            this.addGeographicBounds(Ellipsoid.WGS_84, region[0], region[1], region[2], region[3], region[4], region[5], undefined, color);
        }
        // Render the bounding volume box if present and requested
        if (readBoundingBox(boundingVolume, position, x, y, z) && options.tileBoundingVolumesShowBox) {
            applyMatrixToBox(position, x, y, z, volumeToGlobeTransform);
            applyMatrixToBox(position, x, y, z, globeToWorldTransform);
            DebugDraw.addBox(position, x, y, z, color);
        }
        // We only render the bounding sphere if the bounding volume is actually a sphere, or if the debug flags request
        // rendering the spheres for boxes or regions
        const isRegion = boundingVolume.region !== undefined && boundingVolume.region !== null;
        const isBox = boundingVolume.box !== undefined && boundingVolume.box !== null;
        if (isRegion && !options.tileBoundingVolumesShowRegionSphere)
            return;
        if (isBox && !options.tileBoundingVolumesShowBoxSphere)
            return;
        if (readBoundingSphere(boundingVolume, sphere)) {
            if (typeof volumeToGlobeTransform !== "undefined") {
                sphere.applyMatrix4(volumeToGlobeTransform);
            }
            if (typeof globeToWorldTransform !== "undefined") {
                sphere.applyMatrix4(globeToWorldTransform);
            }
            DebugDraw.addSphere(sphere.center, sphere.radius, color);
        }
    }
    /**
     * Adds a single line segment to the list that will be rendered exactly once in the frame it is requested. The line
     * does not persist between frames.
     *
     * This function must be called every frame for the object to continue drawing. While not very efficient it keeps
     * the usage simple which is perfect for debugging utilities when you just need to get something on screen and don't
     * care how efficient it is
     * @param start The start position of the line segment
     * @param end The end position of the line segment
     * @param color Optional color to apply to the lines
     */
    static addLine(start, end, color = [255, 0, 0]) {
        // Copy into a new vector3 instance
        const startV = new Vector3();
        startV.x = start.x;
        startV.y = start.y;
        startV.z = start.z;
        const endV = new Vector3();
        endV.x = end.x;
        endV.y = end.y;
        endV.z = end.z;
        // Push the beginning and end point of the line
        this.POINTS.push(startV);
        this.COLORS.push(color[0]);
        this.COLORS.push(color[1]);
        this.COLORS.push(color[2]);
        this.POINTS.push(endV);
        this.COLORS.push(color[0]);
        this.COLORS.push(color[1]);
        this.COLORS.push(color[2]);
    }
    /**
     * Adds a single sphere to the list that will be rendered exactly once in the frame it is requested. The sphere does
     * not persist between frames.
     *
     * This function must be called every frame for the object to continue drawing. While not very efficient it keeps
     * the usage simple which is perfect for debugging utilities when you just need to get something on screen and don't
     * care how efficient it is
     * @param position The position of the sphere in world space
     * @param radius The radius of the sphere
     * @param color Optional color to apply to the sphere
     */
    static addSphere(position, radius, color = [255, 0, 0]) {
        const mesh = new Mesh(this.SPHERE_GEOMETRY, this.MESH_MATERIAL.clone());
        mesh.material.color = new Color(color[0] / 255, color[1] / 255, color[2] / 255);
        mesh.frustumCulled = false;
        mesh.scale.x = radius;
        mesh.scale.y = radius;
        mesh.scale.z = radius;
        mesh.position.x = position.x;
        mesh.position.y = position.y;
        mesh.position.z = position.z;
        this.OBJECTS.add(mesh);
    }
    /**
     * Adds a single box to the list that will be rendered exactly once in the frame it is requested. The box does
     * not persist between frames.
     *
     * This function must be called every frame for the object to continue drawing. While not very efficient it keeps
     * the usage simple which is perfect for debugging utilities when you just need to get something on screen and don't
     * care how efficient it is
     * @param position The position of the box in world space
     * @param xAxis Encodes the half width and X axis of the box's rotation basis
     * @param yAxis Encodes the half height and X axis of the box's rotation basis
     * @param zAxis Encodes the half depth and X axis of the box's rotation basis
     * @param color Optional color to apply to the box
     */
    static addBox(position, xAxis, yAxis, zAxis, color = [255, 0, 0]) {
        const xScratch = new Vector3(xAxis.x, xAxis.y, xAxis.z);
        const yScratch = new Vector3(yAxis.x, yAxis.y, yAxis.z);
        const zScratch = new Vector3(zAxis.x, zAxis.y, zAxis.z);
        const mesh = new Mesh(this.BOX_GEOMETRY, this.MESH_MATERIAL.clone());
        mesh.material.color = new Color(color[0] / 255, color[1] / 255, color[2] / 255);
        mesh.frustumCulled = false;
        // Set the position to the provided one
        mesh.position.x = position.x;
        mesh.position.y = position.y;
        mesh.position.z = position.z;
        // Size of the box is defined by the lengths of each axis
        mesh.scale.x = xScratch.length();
        mesh.scale.y = yScratch.length();
        mesh.scale.z = zScratch.length();
        // Normalize the axis to produce a rotation matrix and update the mesh's rotation from that matrix. The only way
        // I can find to set the rotation based on 3 orthogonal basis vectors with ThreeJS.
        xScratch.normalize();
        yScratch.normalize();
        zScratch.normalize();
        const rot = new Matrix4();
        rot.set(xScratch.x, xScratch.y, xScratch.z, 0, yScratch.x, yScratch.y, yScratch.z, 0, zScratch.x, zScratch.y, zScratch.z, 0, 0, 0, 0, 1);
        mesh.quaternion.setFromRotationMatrix(rot.invert());
        this.OBJECTS.add(mesh);
    }
    /**
     * Instantiates a new 'IRenderPass' instance that will handle rendering the debug shapes into the provided render
     * target.
     *
     * It is perfectly valid (and expected) to create multiple pass instances by calling this function multiple times.
     * Specifically it is intended to create one pass for each render target in the back buffer set.
     *
     * @param rt The render target to render the debug geometry into.
     */
    static getRenderPass(rt) {
        return {
            render(threeRenderer, webGLRenderer, camera, _scenes) {
                const colors = new Uint8Array(DebugDraw.COLORS);
                // Build the geometry for lines from our list of points
                DebugDraw.LINE_GEOMETRY.setFromPoints(DebugDraw.POINTS);
                DebugDraw.LINE_GEOMETRY.setAttribute("color", new Uint8BufferAttribute(colors, 3, true));
                // Add the line object to the render set
                DebugDraw.OBJECTS.add(DebugDraw.LINE_OBJECT);
                DebugDraw.OBJECTS.updateMatrixWorld();
                // Render all debug objects
                webGLRenderer.setRenderTarget(rt);
                webGLRenderer.render(DebugDraw.OBJECTS, camera);
                // Once we've rendered this frame's lines we clear the render lists ready for recording the next frame
                DebugDraw.POINTS = [];
                DebugDraw.COLORS = [];
                DebugDraw.OBJECTS.clear();
            },
        };
    }
}
_a = DebugDraw;
/**
 * The material used to render lines with
 * @private
 */
DebugDraw.LINE_MATERIAL = new LineBasicMaterial({ vertexColors: true });
/**
 * Single buffer we use for line geometry
 * @private
 */
DebugDraw.LINE_GEOMETRY = new BufferGeometry();
/**
 * The line object itself that we use for rendering the lines
 * @private
 */
DebugDraw.LINE_OBJECT = _a.lineObjectInit();
/**
 * Point buffer we add the debug line vertices into
 * @private
 */
DebugDraw.POINTS = [];
/**
 * Color buffer we add the debug line vertex colors into
 * @private
 */
DebugDraw.COLORS = [];
/**
 * Material used to render mesh debug shapes with
 * @private
 */
DebugDraw.MESH_MATERIAL = new MeshBasicMaterial({
    color: 0xff0000,
    wireframe: true,
});
/**
 * The single mesh we use to render all debug spheres
 * @private
 */
DebugDraw.SPHERE_GEOMETRY = new SphereGeometry(1);
/**
 * The single mesh we use to render all debug boxes
 * @private
 */
DebugDraw.BOX_GEOMETRY = new BoxGeometry(2, 2, 2);
/**
 * A collection that we accumulate the debug objects into for rendering
 * @private
 */
DebugDraw.OBJECTS = new Object3D();
function applyMatrixToBox(position, x, y, z, matrix) {
    if (typeof matrix !== "undefined") {
        const translation = new Vector3();
        const rotation = new Quaternion();
        const scale = new Vector3();
        position.applyMatrix4(matrix);
        matrix.decompose(translation, rotation, scale);
        x.multiply(scale).applyQuaternion(rotation);
        y.multiply(scale).applyQuaternion(rotation);
        z.multiply(scale).applyQuaternion(rotation);
    }
}
/**
 * Private function used for rendering lines in cartographic space.
 * @param ellipsoid
 * @param start
 * @param end
 * @param segCount
 * @param globeToWorldTransform
 * @param color
 */
function drawCartographicSegments(ellipsoid, start, end, segCount, globeToWorldTransform, color = [255, 0, 0]) {
    const longitudeStep = (end.longitude - start.longitude) / segCount;
    const latitudeStep = (end.latitude - start.latitude) / segCount;
    const heightStep = (end.height - start.height) / segCount;
    const cartographic = new Cartographic();
    const current = new Vector3();
    const last = ellipsoid.cartesianForCartographic(cartographic.set(start.longitude, start.latitude, start.height));
    for (let i = 1; i <= segCount; i += 1) {
        const longitude = start.longitude + longitudeStep * i;
        const latitude = start.latitude + latitudeStep * i;
        const height = start.height + heightStep * i;
        ellipsoid.cartesianForCartographic(cartographic.set(longitude, latitude, height), current);
        if (typeof globeToWorldTransform !== "undefined") {
            current.applyMatrix4(globeToWorldTransform);
        }
        DebugDraw.addLine(last, current, color);
        last.copy(current);
    }
}
/**
 * Private function used for rendering quads in cartographic space. Is a wrapper around four calls to
 * drawCartographicSegments.
 * @param ellipsoid
 * @param west
 * @param south
 * @param east
 * @param north
 * @param height
 * @param segCount
 * @param globeToWorldTransform
 * @param color
 */
function drawCartographicQuadAtHeight(ellipsoid, west, south, east, north, height, segCount, globeToWorldTransform, color = [255, 0, 0]) {
    drawCartographicSegments(ellipsoid, {
        longitude: west,
        latitude: south,
        height,
    }, {
        longitude: east,
        latitude: south,
        height,
    }, segCount, globeToWorldTransform, color);
    drawCartographicSegments(ellipsoid, {
        longitude: west,
        latitude: south,
        height,
    }, {
        longitude: west,
        latitude: north,
        height,
    }, segCount, globeToWorldTransform, color);
    drawCartographicSegments(ellipsoid, {
        longitude: west,
        latitude: north,
        height,
    }, {
        longitude: east,
        latitude: north,
        height,
    }, segCount, globeToWorldTransform, color);
    drawCartographicSegments(ellipsoid, {
        longitude: east,
        latitude: south,
        height,
    }, {
        longitude: east,
        latitude: north,
        height,
    }, segCount, globeToWorldTransform, color);
}
